import { ArrowDownIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { CATEGORICAL, NEUTRAL } from "../../../config/vizPalette";

/**
 * How the Steam insights are built, from API to page. Stages are grouped
 * into ingest -> model -> serve; the colour chip encodes the layer.
 */
const LAYER_COLOURS = {
    source: NEUTRAL,
    stg: CATEGORICAL[1],
    int: CATEGORICAL[2],
    mart: CATEGORICAL[3],
    serve: CATEGORICAL[0],
};

const GROUPS = [
    {
        name: "Ingest",
        stages: [
            {
                layer: "source",
                title: "Steam Web API and Store",
                detail: "Owned games in three passes, player achievements with two fallback endpoints, Store details and global unlock rates.",
            },
            {
                layer: "source",
                title: "Daily refresh",
                detail: "APScheduler at 05:00 UTC. A Postgres advisory lock keeps it to one run at a time; each run is logged to steam_refresh_log.",
            },
            {
                layer: "source",
                title: "Raw tables",
                detail: "Four Postgres tables, upserted in place. They only ever hold the current state.",
            },
        ],
    },
    {
        name: "Model",
        stages: [
            {
                layer: "stg",
                title: "stg_ views",
                detail: "Typed, renamed copies of the raw tables. The only engine-specific SQL (JSON genres, release-date text) lives here.",
            },
            {
                layer: "int",
                title: "int_ views",
                detail: "Achievement totals per game, start-date inference from evidence, and genre affinity.",
            },
            {
                layer: "mart",
                title: "mart_ tables",
                detail: "mart_steam_game and mart_steam_achievement, one row per entity, rebuilt at the end of every refresh.",
            },
        ],
    },
    {
        name: "Serve",
        stages: [
            {
                layer: "serve",
                title: "Flask and registries",
                detail: "Metric and recommendation definitions in etl/steam_insights.py, served at /steam/insights/*.",
            },
            {
                layer: "serve",
                title: "This page",
                detail: "React Query and D3. The metric list comes from the API, so a new definition needs no page change.",
            },
        ],
    },
];

const LEGEND = [
    { layer: "source", label: "Source and raw" },
    { layer: "stg", label: "Staging" },
    { layer: "int", label: "Intermediate" },
    { layer: "mart", label: "Mart" },
    { layer: "serve", label: "Serving" },
];

const Chip = ({ layer }) => (
    <span
        aria-hidden="true"
        className="mt-1.5 inline-block h-3 w-3 flex-none rounded-full"
        style={{ backgroundColor: LAYER_COLOURS[layer] }}
    />
);

export const PipelineDiagram = () => (
    <figure>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
            {GROUPS.map((group, groupIndex) => (
                <div key={group.name} className="flex flex-col gap-2 lg:flex-1 lg:flex-row lg:items-stretch">
                    {groupIndex > 0 && (
                        <div className="flex items-center justify-center text-gray-400" aria-hidden="true">
                            <ArrowDownIcon className="h-5 w-5 lg:hidden" />
                            <ArrowRightIcon className="hidden h-5 w-5 lg:block" />
                        </div>
                    )}
                    <div className="flex-1 rounded-lg border border-gray-200 bg-white p-4">
                        <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{group.name}</p>
                        <ol className="space-y-3">
                            {group.stages.map((stage) => (
                                <li key={stage.title} className="flex gap-3">
                                    <Chip layer={stage.layer} />
                                    <div>
                                        <p className="text-base font-semibold text-gray-800">{stage.title}</p>
                                        <p className="text-sm text-gray-500">{stage.detail}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            ))}
        </div>
        <figcaption className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500">
            {LEGEND.map((item) => (
                <span key={item.layer} className="inline-flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        className="inline-block h-3 w-3 rounded-full"
                        style={{ backgroundColor: LAYER_COLOURS[item.layer] }}
                    />
                    {item.label}
                </span>
            ))}
        </figcaption>
    </figure>
);
